<template>
  <div class="card card-body">
    <paginated-table hover
                     selectable
                     select-mode="single"
                     @pt:row-selected="onSelected"
                     :items="checkableItems"
                     :fields="operativeFields"
                     :load-data="loadData"
                     :tbody-tr-class="rowClass">
      <template v-slot:info-header>
        <i class="pull-right text-info">* {{ inspectionInfo }}</i>
      </template>
      <template v-slot:head(action)>
        <div>
          <div v-if="allowItemCheck && mayAddInspection">
            <div v-if="showAddButton && checkedItems && allowAddInspectionForCheckedItems && checkedItems.length > 0">
              <b-dropdown right variant="primary" size="sm">
                <template v-slot:button-content>
                  <i class="fa fa-fw fa-plus-circle" /> Add
                </template>
                <b-dropdown-item
                  href="#"
                  @click="$emit('add-items', {target: 'default', items: checkedItems})">
                  Add to Inspection
                </b-dropdown-item>
                <b-dropdown-item
                  href="#"
                  @click="$emit('add-items', {target: 'new-inspection', items: checkedItems})">
                  Add to New Inspection
                </b-dropdown-item>
                <b-dropdown-item
                  href="#"
                  @click="$emit('add-items', {target: 'existing-inspection', items: checkedItems})">
                  Add to Existing Inspection
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <div v-else>
              Selected
            </div>
            <div>
              <b-button variant="link" title="Select All" size="xs" @click="selectAll">
                All
              </b-button>|
              <b-button variant="link" title="Select None" size="xs" @click="selectNone">
                None
              </b-button>
            </div>
          </div>
          <div v-else>
            &nbsp;
          </div>
        </div>
      </template>      <template v-slot:cell(action)="data">
        <div class="text-center d-inline">
          <!-- History/Inspection content from parent -->
          <slot name="action-column-content" v-bind="data.item" />
          <!-- Conditional buttons based on deletion status -->
          <div v-if="mayDeleteBuilding && !allowItemCheck && showActionColumn">
            <!-- Undo button for deleted buildings -->
            <b-button 
              v-if="data.item.isDeleted"
              size="sm" 
              variant="warning" 
              class="mr-2" 
              :disabled="isActionLoading(data.item)"
              @click="onRestoreBuilding(data.item)" 
              title="Restore Building">
              <b-spinner v-if="isActionLoading(data.item)" small class="mr-1" />
              <i v-else class="fa fa-undo fa-fw text-white" />
              <span v-if="isActionLoading(data.item)" class="sr-only">Restoring...</span>
            </b-button>
            <!-- Delete button for non-deleted buildings -->
            <b-button 
              v-else
              size="sm" 
              variant="danger" 
              class="mr-2" 
              :disabled="isActionLoading(data.item)"
              @click="onDeleteBuilding(data.item)" 
              title="Delete Building">
              <b-spinner v-if="isActionLoading(data.item)" small class="mr-1" />
              <i v-else class="fa fa-trash fa-fw text-white" />
              <span v-if="isActionLoading(data.item)" class="sr-only">Deleting...</span>
            </b-button>
          </div>
          
          <!-- Checkbox for item selection -->
          <b-checkbox v-if="allowItemCheck" v-model="data.item.checked" />
        </div>
      </template>
      <template v-slot:head(schoolDepSqFt)>
        <div>
          <span>
            School ft
            <sup>2</sup>
          </span>
        </div>
      </template>
      <template v-slot:cell(districtLEA)="data">
        <div>
          <span :id="data.index + '-' + data.value">
            {{ data.value }}
          </span>
          <lea-tooltip :target-selector="data.index + '-' + data.value" :lea-number="data.value" />
        </div>
      </template>
      <template v-slot:cell(schoolNumber)="data">
        <div>
          <span :id="data.index + '-' + data.value">
            {{ data.value }}
          </span>
          <lea-tooltip :target-selector="data.index + '-' + data.value" :lea-number="data.value" />
        </div>
      </template>
      <template v-slot:cell(academicSpace)="data">
        <div class="text-center">
          <span class="fa" :class="{'text-success fa-check-circle': data.value, 'text-danger fa-times-circle': !data.value}" />
        </div>
      </template>
      <template v-slot:cell(latestInspection.components)="data">
        <div>
          <span class="badge badge-warning" v-if="data.value && data.value.some(c => c.rating == RatingTypes.Fix)">FIX</span>
          <span class="badge badge-success" v-else-if="data.value && data.value.every(c => c.rating == RatingTypes.OK || c.rating == RatingTypes.NotApplicable || c.rating == RatingTypes.NotChecked)">OK</span>
          <span class="badge badge-secondary" v-else-if="data.item.latestInspection.status == InspectionStatuses.Skipped">PENDING</span>
          <span class="badge badge-secondary" v-else>PENDING</span>
        </div>
      </template>
      <template v-slot:cell(components)="data">
        <div>
          <span class="badge badge-warning" v-if="data.value && data.value.some(c => c.rating == RatingTypes.Fix)">FIX</span>
          <span class="badge badge-success" v-else-if="data.value && data.value.every(c => c.rating == RatingTypes.OK || c.rating == RatingTypes.NotApplicable || c.rating == RatingTypes.NotChecked)">OK</span>
          <span class="badge badge-secondary" v-else-if="data.item.status == InspectionStatuses.Skipped">PENDING</span>
          <span class="badge badge-secondary" v-else>PENDING</span>
        </div>
      </template>
      <template v-slot:cell(latestInspection.status)="data">
        <div>
          <span class="badge" :class="inspectionStatusDisplayOptions(data.value).badgeClass"><i class="fa-fw" :class="inspectionStatusDisplayOptions(data.value).iconClass" /> {{ inspectionStatusDisplayOptions(data.value).text }}</span>
        </div>
      </template>
      <template v-slot:cell(status)="data">
        <div>
          <span class="badge" :class="inspectionStatusDisplayOptions(data.value).badgeClass"><i class="fa-fw" :class="inspectionStatusDisplayOptions(data.value).iconClass" /> {{ inspectionStatusDisplayOptions(data.value).text }}</span>

          &nbsp;
          <b-badge variant="secondary" v-if="data.value == InspectionStatuses.NotStarted">
            Not Started
          </b-badge>
          <b-badge variant="primary" v-if="data.value == InspectionStatuses.InProgress">
            In Progress
          </b-badge>

          <b-badge variant="secondary" v-if="data.value == InspectionStatuses.Completed && hasNoComponentsChecked(data.item)">
            Not Checked
          </b-badge>

          <b-badge variant="warning" v-if="data.value == InspectionStatuses.Completed && hasFixComponents(data.item)">
            1+ Issues
          </b-badge>
          
          <b-badge variant="success" v-if="data.value == InspectionStatuses.Completed && !hasFixComponents(data.item) && !hasNoComponentsChecked(data.item)">
            No Issues
          </b-badge>
        </div>
      </template>
      <template v-slot:cell(latestInspection.completionDate)="data">
        <div>
          <span v-if="data.value">{{ formatDate(data.value) }}</span>
          <span v-else>N/A</span>
        </div>
      </template>
    </paginated-table>
  </div>
</template>

<style lang='scss'>
.contact-card {
  margin-top: 1.5em;
}

.building-deleted {
  text-decoration: line-through;
  opacity: 0.6;
  color: #6c757d;
}

.building-deleted td {
  text-decoration: line-through;
}
</style>

<script>
import { RatingTypes } from '../common/Enums'
import { TableMixin } from '../../TableMixin'
import { DpsaftListMixin } from './DpsaftListMixin'

export default {
  mixins: [DpsaftListMixin, TableMixin],
  data() {
    return {
      listItemType: 'building',
      actionLoading: {}, // Track loading state for each building action
      fields: [
        {
          key: 'districtLEA',
          sortable: true
        },
        {
          key: 'schoolNumber',
          sortable: true
        },
        {
          key: 'buildingNumber',
          sortable: true
        },
        {
          key: 'buildingName',
          sortable: true
        },
        {
          key: 'academicSpace',
          sortable: true,
          thClass: 'text-center'
        },
        {
          key: 'constructionYear',
          sortable: true
        },
        {
          key: 'schoolDepSqFt',
          sortable: true
        },
        {
          key: 'buildingDepValue',
          label: 'Depreciated Building Value',
          sortable: true,
          formatter(value) {
            return `${value}%`
          }
        },
        {
          key: 'latestInspection.status',
          label: `Inspection Status *`,
          sortable: true,
        },
        {
          key: 'latestInspection.completionDate',
          label: 'Inspection Date *',
          sortable: true
        }
      ]
    }
  },
  methods: {    hasFixComponents(inspection) {
      return inspection.components.some(c => c.rating == RatingTypes.Fix)
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.isDeleted) return 'building-deleted'
    },
    isActionLoading(building) {
      const key = `${building.schoolNumber}-${building.buildingNumber}`
      return this.actionLoading[key] || false
    },
    setActionLoading(building, loading) {
      const key = `${building.schoolNumber}-${building.buildingNumber}`
      this.$set(this.actionLoading, key, loading)
    },
    async onDeleteBuilding(building) {
      console.log(building)
      const confirmed = await this.$bvModal.msgBoxConfirm(`Are you sure you want to delete building "${building.buildingName}" (${building.schoolNumber}/${building.buildingNumber})?`, {
        title: 'Confirm Delete',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      
      if (confirmed) {
        this.setActionLoading(building, true)
        try {
          await this.$store.dispatch('DPSAFT/deleteBuilding', {
            schoolLea: building.schoolNumber,
            buildingNumber: building.buildingNumber
          })
          this.$bvToast.toast(`Building "${building.buildingName}" has been deleted successfully.`, {
            title: 'Building Deleted',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          })
        } catch (error) {
          console.error('Error deleting building:', error)
          this.$bvToast.toast('An error occurred while deleting the building. Please try again.', {
            title: 'Delete Failed',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        } finally {
          this.setActionLoading(building, false)
        }
      }
    },
    async onRestoreBuilding(building) {
      console.log(building)
      const confirmed = await this.$bvModal.msgBoxConfirm(`Are you sure you want to restore building "${building.buildingName}" (${building.schoolNumber}/${building.buildingNumber})?`, {
        title: 'Confirm Restore',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'warning',
        okTitle: 'Restore',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      
      if (confirmed) {
        this.setActionLoading(building, true)
        try {
          await this.$store.dispatch('DPSAFT/restoreBuilding', {
            schoolLea: building.schoolNumber,
            buildingNumber: building.buildingNumber
          })
          this.$bvToast.toast(`Building "${building.buildingName}" has been restored successfully.`, {
            title: 'Building Restored',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          })
        } catch (error) {
          console.error('Error restoring building:', error)
          this.$bvToast.toast('An error occurred while restoring the building. Please try again.', {
            title: 'Restore Failed',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        } finally {
          this.setActionLoading(building, false)
        }
      }
    }
  },
  computed: {
    mayAddInspection() {
      return this.userContext.hasAnyRole(['Insights_DPSAFT_Administrators', 'Insights_DPSAFT_Facilities_Administrators', 'Insights_DPSAFT_Facilities_Area_Managers_Maintenance', 'Insights_DPSAFT_Facilities_Area_Managers_Planning_Construction'])
    },
    mayDeleteBuilding() {
      return this.userContext.isInsightsAdmin || this.userContext.hasAnyRole(['Insights_DPSAFT_Administrators', 'Insights_DPSAFT_Facilities_Administrators', 'Insights_DPSAFT_Facilities_Area_Managers_Maintenance', 'Insights_DPSAFT_Facilities_Area_Managers_Planning_Construction'])
    },
    operativeFields() {
      let fields = this.fields
      if (this.showLatestInspection) {
        fields.push(
          {
            key: 'latestInspection.status',
            label: 'Inspection Status *',
            sortable: true
          },
          {
            key: 'latestInspection.completionDate',
            label: 'Inspection Date *',
            sortable: true
          }
        )
      }
      if (this.showBatchStatus) {
        fields.push({
          key: 'components',
          label: 'Building Status *',
        },
        {
          key: 'status',
          label: 'Inspection Status *',
          sortable: true
        })
      } else {
        fields.push({
          key: 'yearLastInspected',
          label: 'Year Last Inspected',
          sortable: true
        })
      }
      
      if (this.showActionColumn || (this.mayDeleteBuilding && this.selectMode !== 'multiple')) {
        let actionColumn = {
          key: 'action',
          class: 'text-center nowrap', // Add nowrap to prevent wrapping
          thClass: 'text-center nowrap', // Add nowrap to prevent wrapping in header
          width: '100px',
        }
        if (this.allowItemCheck && this.mayAddInspection) {
          actionColumn.label = ' '
        } else if (this.mayDeleteBuilding && this.selectMode !== 'multiple') {
          actionColumn.label = ' '
        } else {
          actionColumn.label = ' '
        } 

        fields.push(actionColumn)
      }
      return fields.filter(f => !this.hideColumns.includes(f.key))
    },
    checkableItems() {
      if (!this.items) return []
      let filteredItems = this.items
      if (this.allowItemCheck) {
        filteredItems = this.items.filter(item => !item.isDeleted)
      }
      return filteredItems.map(item => Object.assign({ checked: false }, item))
    }
  },
  props: {
    allowAddInspectionForCheckedItems: {
      type: Boolean,
      default: false
    },
    showActionColumn: {
      type: Boolean,
      default: false
    },
    showAddButton:{
      type: Boolean,
      default: false
    },
    selectMode: {
      type: String,
      default: 'single'
    },
    hideColumns: {
      type: Array,
      default() { return [] }
    },
  }, 
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.checkableItems = newItems
          ? newItems.map(item => Object.assign({ checked: false }, item))
          : []
      }
    }
  }
}
</script>